import { Instance } from 'mobx-state-tree';
import { Secretariat } from '../../store/models';
import React from 'react';
import { Card, Elevation } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { css } from 'emotion';
import { pulsarLogo } from '../../assets';
import { getSecretariatLogo } from '../../common/secretariatUtils';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import { TLCode } from '../../common/enums';
import { UserGuideLink } from '../common/UserGuideLink';

const CardStyle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;

const centerCaption = css({
  textAlign: 'center',
  position: 'relative',
});

interface SecretariatCardProps extends WithTranslateProps {
  secretariat: Instance<typeof Secretariat> | undefined;
}

const SecretariatCard = ({ secretariat }: SecretariatCardProps) => {
  const secretariatFullName = React.useMemo(() => {
    if (secretariat === undefined) return 'Union Pharmaceutique du Hainaut Occidental et Central';
    return secretariat.name;
  }, [secretariat]);

  const secretariatWebsite = React.useMemo(() => {
    if (secretariat === undefined || secretariat.website === undefined) return 'http://www.uphoc.be';
    return secretariat.website;
  }, [secretariat]);

  const secretariatWebsiteDisplayed = React.useMemo(() => {
    return secretariatWebsite.replace('https://', '').replace('http://', '');
  }, [secretariatWebsite]);

  const secretariatAddressStreet = React.useMemo(() => {
    if (secretariat === undefined) return 'Avenue des Nouvelles Technologies, 59';
    return secretariat.address;
  }, [secretariat]);

  const secretariatLocation = React.useMemo(() => {
    if (secretariat === undefined) return '7080 FRAMERIES';
    return secretariat.zipCode + ' ' + secretariat.city;
  }, [secretariat]);

  const secretariatPhoneNumber = React.useMemo(() => {
    if (secretariat === undefined) return '065/61.19.43';
    return secretariat.phoneNumber;
  }, [secretariat]);

  const secretariatMail = React.useMemo(() => {
    if (secretariat === undefined) return 'garde@uphoc.be';
    return secretariat.mailAddress;
  }, [secretariat]);

  return (
    <Card className={CardStyle.toString()} interactive={false} elevation={Elevation.TWO}>
      <DivLine>
        <DivLeft>
          <h2>{secretariatFullName}</h2>
        </DivLeft>
        <DivRight>
          <a href={secretariatWebsite} target="_blank" rel="noopener noreferrer">
            <figure>
              <img src={getSecretariatLogo(secretariat)} height={50} alt="SecretariatLogo" />
              <figcaption className={centerCaption.toString()}>click me</figcaption>
            </figure>
          </a>
        </DivRight>
      </DivLine>
      <br />
      <h3>{secretariatAddressStreet}</h3>
      <h3>{secretariatLocation}</h3>
      <br />
      <a href={secretariatWebsite} target="_blank" rel="noopener noreferrer">
        <u>{secretariatWebsiteDisplayed}</u>
      </a>
      <br />
      <br />
      <h3>
        <TranslatedMessage id={TLCode.ContactUs} />
      </h3>
      <DivLine>
        <DivLeft>
          <h3><p>
            <TranslatedMessage id={TLCode.ByPhone} /> {secretariatPhoneNumber}
          </p></h3>
          <h3><p>
            <TranslatedMessage id={TLCode.ByMail} /> <a href={'mailto:' + secretariatMail}>{secretariatMail}</a>
          </p></h3>
        </DivLeft>
        <DivRight>
          <a href="http://www.pulsar.be" target="_blank" rel="noopener noreferrer">
            <figure>
              <img src={pulsarLogo} height={50} alt="PulsarLogo" />
              <figcaption className={centerCaption.toString()}>click me</figcaption>
            </figure>
          </a>{' '}
        </DivRight>
      </DivLine>
      <UserGuideLink UserGuideLabel="Guide Utilisateur" />
      <br />
      <br />
    </Card>
  );
};

export default withTranslate(SecretariatCard);
